import { useWindowResize } from '@toggle/helpers';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useRef } from 'react';

import { getThemeColor } from '~/design-tokens/themes/theme-utils';
import { DesignToken, ThemeName } from '~/design-tokens/themes/themes';

import { DEFAULT_CHART_WIDTH, LEGEND_FONT_SIZE } from './apache-config';

export interface LegendItem {
  name: string;
  type: 'line' | 'bar' | 'scatter';
}

const DEFAULT_ITEM_WIDTH = 150;
const DEFAULT_CONTAINER_HEIGHT = 50;
const DEFAULT_ITEM_GAP = 15;
const DEFAULT_ROW_HEIGHT = 20;
const MARGIN_RIGHT = '4%';

const getContainerHeight = (legendData: LegendItem[], width: number) => {
  const itemsPerRow = Math.max(1, Math.floor(width / DEFAULT_ITEM_WIDTH));
  const rowsCount = Math.ceil(legendData.length / itemsPerRow);
  return Math.max(
    DEFAULT_CONTAINER_HEIGHT,
    rowsCount * DEFAULT_ROW_HEIGHT + DEFAULT_ITEM_GAP
  );
};

export interface LegendsForCopyingProps {
  theme: ThemeName;
  legendData: LegendItem[];
  targetChart: Types.ECharts;
  zAxisInfo?: string;
}

export const RemoteLegend = ({
  theme,
  legendData,
  targetChart,
  zAxisInfo,
}: LegendsForCopyingProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width } = useWindowResize({ targetRef: ref });

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height: DEFAULT_CONTAINER_HEIGHT });
    }
  }, [width]);

  useEffect(() => {
    const currentWidth = width || DEFAULT_CHART_WIDTH;
    const estimatedHeight = getContainerHeight(legendData, currentWidth);

    const chartDom = init(ref.current, theme, {
      width: currentWidth,
      height: estimatedHeight,
    });

    const defaultLegendConfig: Types.LegendComponentOption = {
      type: 'plain',
      orient: 'horizontal',
      right: MARGIN_RIGHT,
      top: 'top',
      itemGap: DEFAULT_ITEM_GAP,
      textStyle: {
        color: getThemeColor('--border-default', theme),
        fontSize: LEGEND_FONT_SIZE,
      },
      tooltip: {
        show: true,
      },
    };

    const series: Types.SeriesOption[] = legendData.map((item, index) => ({
      name: item.name,
      type: item.type,
      data: [],
      itemStyle: {
        color: getThemeColor(
          `--chart-multi-lines-hex-${(index % 12) + 1}` as DesignToken,
          theme
        ),
      },
      lineStyle: {
        width: 2,
      },
    }));

    const option: Types.EChartsOption = {
      backgroundColor: 'transparent',
      xAxis: {
        show: false,
        type: 'category',
      },
      yAxis: {
        show: false,
        type: 'value',
      },
      legend: {
        ...defaultLegendConfig,
      },
      series,
      ...(zAxisInfo && {
        title: {
          text: zAxisInfo,
          bottom: 0,
          right: MARGIN_RIGHT,
          textStyle: {
            color: getThemeColor('--border-default', theme),
            fontSize: LEGEND_FONT_SIZE,
            fontWeight: 400,
          },
        },
      }),
    };

    chartDom.setOption(option);

    if (targetChart) {
      chartDom.on('legendselectchanged', params => {
        targetChart.dispatchAction({
          type: 'legendToggleSelect',
          name: (params as unknown as Types.SelectChangedEvent).name,
        });
      });
    }

    return () => {
      chartDom.dispose();
    };
  }, [theme, legendData, targetChart, width, zAxisInfo]);

  return <div ref={ref} className="legend-container" />;
};
